import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import { cn } from '@/app/utils';

const DEFAULT_MIN_TEXTAREA_HEIGHT = 116;

const nextFrame = (callback: () => void) => {
  requestAnimationFrame(() => requestAnimationFrame(callback));
};

const getElementOuterHeight = (element: HTMLElement | null) => {
  if (!element) return 0;

  const style = window.getComputedStyle(element);
  return element.offsetHeight + Number.parseFloat(style.marginTop) + Number.parseFloat(style.marginBottom);
};

export const BasicTextarea = forwardRef<
  HTMLTextAreaElement,
  {
    textPrompt: string;
    updateTextPrompt: (value: string) => void;
    showClearButton: boolean;
    onClearPrompt: () => void;
    placeholder: string;
    extraContent?: React.ReactNode;
    className?: string;
    fitContainer?: boolean;
    minHeight?: number;
    /** `false`：不设置原生 maxlength，由父组件自行限制（如按汉字加权长度） */
    maxLength?: number | false;
  }
>(
  (
    {
      textPrompt,
      updateTextPrompt,
      showClearButton,
      onClearPrompt,
      placeholder,
      extraContent,
      className,
      fitContainer = false,
      minHeight = DEFAULT_MIN_TEXTAREA_HEIGHT,
      maxLength,
    },
    ref,
  ) => {
    const t = useTranslations();
    const limit = maxLength === false ? undefined : (maxLength ?? MAX_PROMPT_LENGTH);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const actionsRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [availableHeight, setAvailableHeight] = useState<number | null>(null);
    const [isPanelAtMaxHeight, setIsPanelAtMaxHeight] = useState(false);

    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const updateTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.style.height = 'auto';
      if (fitContainer && availableHeight) {
        const rootElement = rootRef.current;
        const contentHeight = Math.max(textarea.scrollHeight, minHeight);
        const panel = rootElement?.closest('[data-create-input-panel="true"]');
        const maxHeight = panel ? Number.parseFloat(window.getComputedStyle(panel).maxHeight) : Number.NaN;
        const maxTextareaHeight =
          panel && rootElement && Number.isFinite(maxHeight)
            ? Math.max(
                maxHeight - (panel.scrollHeight - rootElement.clientHeight) - getElementOuterHeight(actionsRef.current),
                minHeight,
              )
            : Number.POSITIVE_INFINITY;
        const targetHeight = textPrompt.length === 0 ? minHeight : contentHeight;
        textarea.style.height = `${Math.min(targetHeight, maxTextareaHeight)}px`;
        return;
      }
      textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), 320)}px`;
    };

    useEffect(() => {
      updateTextareaHeight();
    }, [availableHeight, fitContainer, isPanelAtMaxHeight, minHeight, textPrompt]);

    useLayoutEffect(() => {
      if (!fitContainer || !rootRef.current) return;

      const updateAvailableHeight = () => {
        const rootHeight = rootRef.current?.clientHeight ?? 0;
        const actionsHeight = actionsRef.current?.offsetHeight ?? 0;
        setAvailableHeight(Math.max(rootHeight - actionsHeight - 8, minHeight));

        const panel = rootRef.current?.closest('[data-create-input-panel="true"]');
        if (!panel) {
          setIsPanelAtMaxHeight(false);
          return;
        }

        const maxHeight = Number.parseFloat(window.getComputedStyle(panel).maxHeight);
        setIsPanelAtMaxHeight(Number.isFinite(maxHeight) && panel.clientHeight >= maxHeight - 1);
      };

      updateAvailableHeight();
      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const resizeObserver = new ResizeObserver(updateAvailableHeight);
      resizeObserver.observe(rootRef.current);
      const panel = rootRef.current.closest('[data-create-input-panel="true"]');
      if (panel) {
        resizeObserver.observe(panel);
      }
      if (actionsRef.current) {
        resizeObserver.observe(actionsRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [fitContainer, extraContent, minHeight, showClearButton]);

    const content = (
      <>
        <Textarea
          className={cn(
            'min-h-[116px] w-full resize-none overflow-auto rounded-none border-none bg-transparent p-0 text-sm text-system-white caret-system-blue02 placeholder:text-system-white36 focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-sm lg:text-xs',
            fitContainer ? 'max-h-full flex-[1_0_auto]' : 'max-h-[320px]',
            className,
          )}
          ref={textareaRef}
          value={textPrompt}
          placeholder={placeholder}
          maxLength={limit}
          onChange={(e) => {
            const { value } = e.target;
            if (limit !== undefined && value.length >= limit) {
              toast({
                type: 'warning',
                message: t('create.createForm.error.invalidLength', { limit }),
              });
            }
            updateTextPrompt(value);
          }}
          required
          style={{ minHeight }}
        />
        <div ref={actionsRef} className="mt-2 flex justify-end gap-3">
          {extraContent}
          {showClearButton && (
            <BaseLineDelete
              className="cursor-pointer text-base"
              onClick={() => {
                onClearPrompt();
                nextFrame(updateTextareaHeight);
              }}
            />
          )}
        </div>
      </>
    );

    if (!fitContainer) {
      return content;
    }

    return (
      <div ref={rootRef} className="flex min-h-[116px] flex-[1_0_auto] flex-col" style={{ minHeight }}>
        {content}
      </div>
    );
  },
);

BasicTextarea.displayName = 'BasicTextarea';
